// src/components/OtpInput.jsx
// ============================================================
// Seis casilleros para un código de un solo uso.
//
// Gemelo del de fixo-mobile (components/OtpInput.tsx): misma API —value,
// onChange, onComplete, disabled— para que las dos plataformas se lean igual
// y un cambio de comportamiento se pueda replicar sin traducir.
//
// Es PRESENTACIONAL: no conoce `api` ni el router. Quién pide el código y a
// dónde se va después lo decide la página que lo usa.
//
// Lo que tiene de más que el de mobile, porque el medio es distinto: pegar.
// En el teléfono el código se tipea; en la compu se copia del mail. Sin
// onPaste, pegar "424242" mete los seis dígitos en el primer casillero,
// maxLength recorta a uno, y el usuario ve un "4" y ningún error.
// ============================================================

import { useRef } from 'react';
import { Box, TextField } from '@mui/material';

export const OTP_LARGO = 6;

/** Estado inicial listo para usar: `useState(otpVacio())`. */
export const otpVacio = () => Array(OTP_LARGO).fill('');

export default function OtpInput({ value, onChange, onComplete, disabled }) {
  const inputs = useRef([]);

  const foco = (i) => inputs.current[i]?.focus();

  function handleChange(val, index) {
    // Un solo dígito. inputMode="numeric" es una sugerencia al teclado, no
    // una restricción: en desktop se puede escribir cualquier cosa.
    if (!/^\d?$/.test(val)) return;

    const next = [...value];
    next[index] = val;
    onChange(next);

    if (val && index < OTP_LARGO - 1) foco(index + 1);

    if (val && index === OTP_LARGO - 1) {
      const completo = next.join('');
      if (completo.length === OTP_LARGO) onComplete?.(completo);
    }
  }

  function handleKeyDown(e, index) {
    // Backspace en un casillero vacío vuelve al anterior.
    if (e.key === 'Backspace' && !value[index] && index > 0) foco(index - 1);
    // Flechas: navegar entre casilleros es lo esperable en un teclado físico.
    if (e.key === 'ArrowLeft'  && index > 0) foco(index - 1);
    if (e.key === 'ArrowRight' && index < OTP_LARGO - 1) foco(index + 1);
  }

  function handlePaste(e) {
    const pegado = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
    if (!pegado) return;
    e.preventDefault();

    const digitos = pegado.slice(0, OTP_LARGO).split('');
    const next = otpVacio();
    digitos.forEach((d, i) => { next[i] = d; });
    onChange(next);

    // Foco en el primero que quedó vacío, o en el último si se llenó todo.
    const siguiente = Math.min(digitos.length, OTP_LARGO - 1);
    foco(siguiente);

    if (digitos.length === OTP_LARGO) onComplete?.(next.join(''));
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.2, my: 1 }}>
      {value.map((digit, i) => (
        <TextField
          key={i}
          inputRef={el => { inputs.current[i] = el; }}
          value={digit}
          onChange={e => handleChange(e.target.value, i)}
          onKeyDown={e => handleKeyDown(e, i)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            inputMode: 'numeric',
            maxLength: 1,
            'aria-label': `Dígito ${i + 1} de ${OTP_LARGO}`,
            style: {
              textAlign: 'center',
              fontSize: 22,
              fontWeight: 600,
              padding: '14px 0',
              width: 44,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
              '& fieldset': { borderColor: digit ? '#E7A432' : '#DDD', borderWidth: digit ? 2 : 1 },
            },
          }}
        />
      ))}
    </Box>
  );
}
